import type { AvailabilityProduct } from '@travelity/api/services';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { PackageBookingFormValues } from '../../package-form.helpers';
import CapacityDropdownRow from './CapacityDropdownRow';
import { buildCapacityFormRows } from './capacity-form.helpers';

interface PackageCapacityProps {
    selectedEvent: AvailabilityProduct;
}

const PackageCapacity = ({ selectedEvent }: PackageCapacityProps) => {
    const { control } = useFormContext<PackageBookingFormValues>();
    const rows = useMemo(
        () => buildCapacityFormRows(selectedEvent),
        [selectedEvent]
    );
    const [expanded, setExpanded] = useState(false);

    return (
        <Stack gap={1}>
            <Typography
                sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                }}
            >
                Capacity
            </Typography>
            <Accordion
                disableGutters
                expanded={expanded}
                onChange={(_, isExpanded) => setExpanded(isExpanded)}
                sx={{
                    p: 1,
                    bgcolor: '#F4F6FA',
                    border: '1px solid #E5E7EB',
                    borderRadius: 1,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        px: 1,
                        minHeight: 'unset',
                        background: '#F4F6FA',
                        '& .MuiAccordionSummary-content': {
                            my: 1,
                            alignItems: 'center',
                            gap: 1,
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        Multiple Capacities
                    </Typography>
                    <Chip
                        label={`${rows.length} Products`}
                        size="small"
                        sx={{
                            height: 22,
                            '& .MuiChip-label': {
                                px: 1,
                                fontSize: '12px',
                                fontWeight: 500,
                            },
                        }}
                    />
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        px: 1,
                        background: '#F4F6FA',
                    }}
                >
                    <Stack sx={{ width: 1, pr: 1.5 }}>
                        {rows.map((row, index) => (
                            <Stack key={row.key}>
                                <Controller
                                    name={
                                        `vehicleByProductId.${row.key}` as const
                                    }
                                    control={control}
                                    render={({ field }) => {
                                        const selectedVehicle =
                                            field.value || '';
                                        const remaining =
                                            row.remainingByCapacity[
                                                selectedVehicle
                                            ] ?? 0;

                                        return (
                                            <CapacityDropdownRow
                                                title={row.title}
                                                subtitle={row.subtitle}
                                                vehicleValue={selectedVehicle}
                                                vehicleOptions={
                                                    row.capacityOptions
                                                }
                                                remaining={remaining}
                                                onVehicleChange={field.onChange}
                                                disabled={
                                                    row.capacityOptions
                                                        .length <= 1
                                                }
                                            />
                                        );
                                    }}
                                />
                                {index < rows.length - 1 && <Divider />}
                            </Stack>
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Stack>
    );
};

export default PackageCapacity;
